import type { LayerConfig } from '../../api/types';
import { browserStorage } from '../visibilityMemory';

const STORAGE_KEY = 'mm_water_control';

export interface WaterControlMemory {
  /** 0..100; 0 means as styled. */
  translucency: number;
}

export function isWaterLayer(layer: Pick<LayerConfig, 'tags'> | undefined): boolean {
  return !!layer?.tags?.includes('water');
}

export function waterLayerIds(layers: ReadonlyMap<string, LayerConfig>): string[] {
  return [...layers.entries()].filter(([, l]) => isWaterLayer(l)).map(([id]) => id);
}

/** BIM model-layer row ids that are water surfaces (the pack water row). */
export const BIM_WATER_LAYER_ID = 'water';

export function isBimWaterSurface(layerId: string): boolean {
  return layerId === BIM_WATER_LAYER_ID;
}

export function loadWaterMemory(): WaterControlMemory {
  try {
    const raw = browserStorage()?.getItem(STORAGE_KEY);
    if (!raw) return { translucency: 0 };
    const parsed = JSON.parse(raw) as { translucency?: unknown };
    const t = Number(parsed.translucency);
    return { translucency: Number.isFinite(t) ? Math.min(100, Math.max(0, t)) : 0 };
  } catch {
    return { translucency: 0 };
  }
}

export function saveWaterMemory(mem: WaterControlMemory): void {
  try {
    browserStorage()?.setItem(STORAGE_KEY, JSON.stringify(mem));
  } catch {
    // blocked storage: in-memory only for this session
  }
}

/** Map 0..100 translucency to a 0..1 opacity multiplier (0% translucency = 1). */
export function translucencyToOpacity(translucency: number): number {
  const t = Math.min(100, Math.max(0, translucency));
  return 1 - t / 100;
}

export function waterGroupVisible(
  ids: readonly string[],
  visibility: Readonly<Record<string, boolean>>,
  defaults: Readonly<Record<string, boolean>>,
): boolean {
  if (!ids.length) return true;
  return ids.every((id) => visibility[id] ?? defaults[id] ?? false);
}
